import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Printer } from 'lucide-react';
import type { ItemVariant, POSItem } from '@/types';
import { posApi } from '@/services/api';
import { printBarcodeLabels } from '@/utils/barcodeLabelPrint';
import { formatMoney } from '@/utils/currency';

interface PrintBarcodeModalProps {
  item: POSItem;
  variants?: ItemVariant[];
  onClose: () => void;
}

export default function PrintBarcodeModal({ item, variants = [], onClose }: PrintBarcodeModalProps) {
  const options = useMemo(() => {
    if (item.has_variants && variants.length) {
      return variants.map((v) => ({
        key: v.name,
        label: v.variant_name,
        barcode: v.barcode || '',
        price: v.price,
        subtitle: item.item_name,
      }));
    }
    return [{
      key: item.name,
      label: item.item_name,
      barcode: item.barcode || '',
      price: item.sale_price,
      subtitle: undefined as string | undefined,
    }];
  }, [item, variants]);

  const [selectedKey, setSelectedKey] = useState(options[0]?.key || '');
  const [copies, setCopies] = useState(1);

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message;
    },
  });

  const selected = options.find((o) => o.key === selectedKey) || options[0];

  const handlePrint = () => {
    if (!selected?.barcode) {
      alert('Add or generate a barcode before printing labels.');
      return;
    }
    const count = Math.max(1, Math.min(500, copies));
    const labels = Array.from({ length: count }, () => ({
      title: selected.subtitle || selected.label,
      subtitle: selected.subtitle ? selected.label : undefined,
      price: selected.price,
      barcode: selected.barcode,
    }));
    void printBarcodeLabels(labels, {
      codeType: posSettings?.use_barcode_or_qr,
      printPrice: posSettings?.print_price_on_label,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Print barcode labels</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Print multiple stickers with the <strong>same</strong> barcode (one code per product or option).
          </p>

          {item.has_variants && options.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Option</label>
              <select
                className="input-field"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}{o.barcode ? '' : ' (no barcode)'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of labels</label>
            <input
              type="number"
              min={1}
              max={500}
              className="input-field"
              value={copies}
              onChange={(e) => setCopies(parseInt(e.target.value, 10) || 1)}
            />
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
            <div className="font-medium text-gray-900">{selected?.subtitle || selected?.label}</div>
            {selected?.subtitle && (
              <div className="text-gray-600">{selected.label}</div>
            )}
            <div className="text-gray-700 mt-1">
              Barcode: {selected?.barcode || <span className="text-amber-700">Not set</span>}
            </div>
            {selected?.price != null && (
              <div className="text-gray-700">Price: {formatMoney(selected.price)}</div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!selected?.barcode}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
